import { asyncify, doUntil } from 'async';
import { useCallback, useMemo } from 'react';

import { Cursor, ListField } from '../models/Repository';
import { FetchActionResult } from '../models/Store';
import useLatestState from './useLatesetState';
import useAbortControllerSignal from './useAbortControllerSignal';

export enum FetchStatus {
  Idle = 0,
  FetchingAll = 1,
  FetchingMore = 2,
  Refreshing = 3,
}
export type FetchListFn<R extends FetchActionResult = FetchActionResult> = (
  cursor: Cursor,
  signal: AbortSignal,
) => Promise<R>;
export type UseFetchList = {
  fetchAll: () => Promise<void>;
  fetchMore: () => Promise<void>;
  refresh: () => Promise<void>;
  isFetchingAll: boolean;
  isFetchingMore: boolean;
  isRefreshing: boolean;
  isFinished: boolean;
};

const useFetchList = <R extends FetchActionResult>(
  field: ListField<unknown> | undefined,
  fetch: FetchListFn<R> | undefined,
): UseFetchList => {
  const signal = useAbortControllerSignal();
  const [status, setStatus] = useLatestState<FetchStatus>(FetchStatus.Idle);
  const [isFinished, setIsFinished] = useLatestState<boolean>(false);

  const fetchData = useCallback(
    (mode: Exclude<FetchStatus, FetchStatus.Idle>) => async () => {
      if (field && fetch) {
        let cursor =
          mode === FetchStatus.FetchingMore ? field.getCursor() : undefined;

        // avoid fetch if reach end or is fetching
        if (cursor === null || setStatus((s) => s)) {
          setIsFinished(true);
          return;
        }

        // update status to be fetching
        setStatus(mode);

        let shouldFetchMore: boolean;
        // use cursor to fetch data until end or server has return useful data
        try {
          await doUntil(
            asyncify(async () => {
              ({ shouldFetchMore } = await fetch(cursor, signal));
              cursor = field.getCursor();
              if (mode === FetchStatus.FetchingAll) {
                shouldFetchMore = !!cursor;
              }
            }),
            asyncify(() => !shouldFetchMore || signal.aborted),
          );
        } catch (err) {
          if (!signal.aborted) {
            throw err;
          }
        } finally {
          setStatus(FetchStatus.Idle);
        }
      }
    },
    [field, fetch, setStatus, setIsFinished, signal],
  );
  const fetchAll = useMemo(
    () => fetchData(FetchStatus.FetchingAll),
    [fetchData],
  );
  const fetchMore = useMemo(
    () => fetchData(FetchStatus.FetchingMore),
    [fetchData],
  );
  const refresh = useMemo(() => fetchData(FetchStatus.Refreshing), [fetchData]);

  return {
    fetchAll,
    fetchMore,
    refresh,
    isFetchingAll: status === FetchStatus.FetchingAll,
    isFetchingMore: status === FetchStatus.FetchingMore,
    isRefreshing: status === FetchStatus.Refreshing,
    isFinished: isFinished,
  };
};

export default useFetchList;
